import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Loader2, Send, CalendarClock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import { useLenis } from "@/hooks/usePageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const ExpertiseDetail = () => {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company_or_role: "", message: "" });
  const [sending, setSending] = useState(false);
  useLenis();

  useEffect(() => {
    window.scrollTo(0, 0);
    setTopic(null);
    setNotFound(false);
    axios
      .get(`${API}/expertise/${slug}`)
      .then((r) => {
        setTopic(r.data);
        setForm((f) => ({ ...f, message: `I'd like to discuss ${r.data.domain} for our landscape. ` }));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        inquiry_type: "S/4HANA Consulting / Advisory",
        company_or_role: form.company_or_role || null,
        message: form.message,
      });
      toast.success("Inquiry sent", { description: "Vikram's team will get back to you within one business day." });
      setForm((f) => ({ ...f, name: "", email: "", company_or_role: "" }));
    } catch {
      toast.error("Could not send inquiry", { description: "Please check the fields and try again." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-600/60 transition-colors";

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="expertise-not-found">
        <p className="font-display text-2xl font-bold">Topic not found</p>
        <Link to="/consulting" className="bg-amber-400 text-slate-900 font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-amber-500 transition-colors">
          Back to Consulting
        </Link>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="expertise-loading">
        <Loader2 size={32} className="text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen" data-testid="expertise-detail-page">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <Link to="/consulting#expertise" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors mb-12" data-testid="expertise-back-link">
          <ArrowLeft size={15} /> Back to expertise
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="lg:col-span-8">
            <span className="inline-block font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-700 border border-amber-600/30 bg-amber-400/10 px-3 py-1 rounded-full mb-5">
              {topic.level}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-6" data-testid="expertise-detail-title">
              {topic.domain}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">{topic.description}</p>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{topic.overview}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }} className="lg:col-span-4">
            <div className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-5">What clients get</p>
              <ul className="space-y-4">
                {(topic.outcomes || []).map((o) => (
                  <li key={o} className="flex items-start gap-3">
                    <BadgeCheck size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <span className="text-sm text-slate-700 leading-relaxed">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-24"
        >
          <h2 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6">Capability areas</h2>
          <div className="border-t border-slate-200">
            {(topic.capabilities || []).map((c, i) => (
              <div key={c} className="flex items-center gap-5 py-5 border-b border-slate-200 group" data-testid={`expertise-capability-${i}`}>
                <span className="font-mono2 text-2xl font-bold text-slate-900/[0.07] group-hover:text-amber-600/40 transition-colors">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm sm:text-base text-slate-800">{c}</span>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8 lg:p-12"
          data-testid="expertise-contact-section"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-3">Contact Us</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">Discuss {topic.domain}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-8">
                Tell us where your landscape stands — you'll hear back within one business day with next steps.
              </p>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                className="inline-flex items-center gap-2 border border-slate-300 rounded-full px-6 py-3 text-sm text-slate-700 hover:border-amber-600/50 hover:text-amber-700 transition-colors"
                data-testid="expertise-book-call-button"
              >
                <CalendarClock size={15} /> Or book a free 15-min call
              </button>
            </div>
            <form onSubmit={submit} className="lg:col-span-7 space-y-4" data-testid="expertise-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className={inputCls} data-testid="expertise-form-name-input" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email *" className={inputCls} data-testid="expertise-form-email-input" />
              </div>
              <input value={form.company_or_role} onChange={(e) => setForm({ ...form, company_or_role: e.target.value })} placeholder="Company / your role" className={inputCls} data-testid="expertise-form-company-input" />
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="expertise-form-message-input" />
              <button
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-9 py-4 rounded-full hover:bg-amber-500 disabled:opacity-60 transition-colors"
                data-testid="expertise-form-submit-button"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          </div>
        </motion.section>
      </div>
      <Footer />
      <BookingModal />
    </div>
  );
};

export default ExpertiseDetail;
